import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS, ROUTES } from '../constants';

export interface User {
  id: number;
  name: string;
  email: string;
  permissions?: string[];
  roles?: string[];
}

// Access token в памяти (не в localStorage — только refresh в cookie)
let accessToken: string | null = null;

const authApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
  withCredentials: true,
});

export const getAccessToken = (): string | null => accessToken;

export const setAccessToken = (token: string | null): void => {
  accessToken = token;
};

export const clearAccessToken = (): void => {
  accessToken = null;
};

/**
 * Декодирует JWT токен и извлекает payload
 * JWT формат: header.payload.signature
 */
const decodeJWT = (token: string): any => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      throw new Error('Invalid JWT format');
    }
    
    // Декодируем payload (вторая часть)
    const payload = parts[1];
    // JWT использует base64url encoding, нужно заменить символы
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/** Обновление Access-токена по Refresh-куке */
export const refresh = async (): Promise<string> => {
  const { data } = await authApi.post<{ accessToken: string }>(API_ENDPOINTS.AUTH.REFRESH);
  const token = data?.accessToken;
  if (!token) {
    throw new Error('Refresh: accessToken не получен');
  }
  setAccessToken(token);
  return token;
};

export const authService = {
  /** POST /api/v1/admin/auth/login — при успехе выставляется кука с Refresh-токеном */
  login: async (login: string, password: string) => {
    const response = await authApi.post<{ accessToken?: string } | string>(API_ENDPOINTS.AUTH.LOGIN, {
      login,
      password,
    });
    const data = response.data;
    // Бэкенд мог вернуть HTML (редирект на свою страницу логина) — не парсим как JSON
    if (typeof data === 'string' && (data.includes('<!') || data.includes('<html'))) {
      throw new Error('Неверный логин или пароль');
    }
    const payload = data as { accessToken?: string };
    // Если бэкенд сразу отдаёт accessToken — сохраняем
    if (payload?.accessToken) {
      setAccessToken(payload.accessToken);
    } else {
      // Иначе получаем его через refresh (кука уже установлена)
      await refresh();
    }
    return payload;
  },

  /** Обновление Access-токена (по Refresh-куке) */
  refresh,

  /** Заглушка — эндпоинта регистрации в текущем API нет */
  register: async (_name: string, _email: string, _password: string, _password_confirmation: string) => {
    throw new Error('Registration is not available');
  },

  /** Выход. Редирект всегда на текущий origin (не на домен API). */
  logout: (sessionExpired = false) => {
    clearAccessToken();
    const path = sessionExpired ? ROUTES.LOGIN_SESSION_EXPIRED : ROUTES.LOGIN;
    window.location.href = `${window.location.origin}${path}`;
  },

  getAccessToken,
  setAccessToken,
  clearAccessToken,

  /** Есть токен — считаем залогиненным. Декодируем JWT для получения данных пользователя. */
  getCurrentUser: async (): Promise<User | null> => {
    const token = getAccessToken();
    if (!token) return null;

    // Декодируем JWT токен
    const decoded = decodeJWT(token);
    if (!decoded) {
      // Если не удалось декодировать, возвращаем null
      return null;
    }

    // Извлекаем данные из payload согласно новой структуре
    // {
    //   "userId": "1",
    //   "roleName": "Admin",
    //   "permissions": ["categories.create", "categories.read", ...]
    // }
    const userId = decoded.userId || decoded.sub || decoded.id || decoded.user_id || 0;
    const userEmail = decoded.email || decoded.user_email || '';
    
    // Извлекаем роль (roleName - это строка, не массив)
    let roles: string[] = [];
    if (decoded.roleName) {
      roles = [decoded.roleName];
    } else if (decoded.roles) {
      roles = Array.isArray(decoded.roles) ? decoded.roles : [decoded.roles];
    } else if (decoded.role) {
      roles = Array.isArray(decoded.role) ? decoded.role : [decoded.role];
    }

    // Извлекаем permissions (новый формат: resource.action)
    let permissions: string[] = [];
    if (decoded.permissions) {
      permissions = Array.isArray(decoded.permissions) ? decoded.permissions : [decoded.permissions];
    }

    // Для имени пользователя используем userId или email, так как в токене нет name
    // В будущем можно добавить запрос к API для получения полной информации о пользователе
    const userName = decoded.name || decoded.username || decoded.user_name || `User ${userId}`;

    return {
      id: typeof userId === 'number' ? userId : parseInt(String(userId)) || 0,
      name: String(userName),
      email: String(userEmail),
      roles: roles.length > 0 ? roles : undefined,
      permissions: permissions.length > 0 ? permissions : undefined,
    };
  },

  /**
   * Проверка сессии: нет токена — пробуем refresh по куке; потом проверяем наличие токена.
   */
  checkSession: async (): Promise<User | null> => {
    if (!getAccessToken()) {
      try {
        await refresh();
      } catch {
        return null;
      }
    }
    return authService.getCurrentUser();
  },
};
